import api from "@/config/api";
import { auth } from "@/config/firebase";
import { useUser } from "@/context/user";
import { Forms } from "@/types/form";
import { setCurrentUser } from "@/utils/helpers";
import { log } from "@/utils/logger";
import { User, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { useState } from "react";

const useLogin = (startCountdown: () => void) => {
    const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
    const setLoading = useUser(state => state.setGlobalLoading);
    const logout = useUser(state => state.logout);
    const savedRoute = sessionStorage.getItem("savedRoute");

    const onLogin = async (data: Forms["Login"]) => {
        try {
            setLoading(true);

            const { email, password } = data;
            const { user } = await signInWithEmailAndPassword(auth, email, password);
            const emailVerified = user.emailVerified;

            if (!emailVerified) {
                await api.post("/users/verify-email", { data: { email }, authenticate: false });
                setEmailVerified(false), startCountdown();
                throw new Error(
                    `You must verify your email. Verification link has been sent to ${email}. $sentry-ignore`,
                );
            }

            await setCurrentUser(user);

            if (!savedRoute) return;
            sessionStorage.removeItem("savedRoute");
        } catch (e) {
            log.error(e, "onLogin");
            await signOut(auth), logout();
        } finally {
            setLoading(false);
        }
    };

    return { onLogin, emailVerified, setEmailVerified };
};

const useResendVerification = () => {
    const setLoading = useUser(state => state.setGlobalLoading);

    const onResendVerification = async () => {
        try {
            setLoading(true);
            const user = auth.currentUser as User;
            await api.post("/users/verify-email", {
                data: { email: user.email },
                authenticate: false,
            });
            log.success("Verification link has been successfully sent.");
        } catch (e) {
            log.error(e, "onResendVerification");
        } finally {
            setLoading(false);
        }
    };

    return onResendVerification;
};

export { useLogin, useResendVerification };
